import { TicketTransfer, TicketTransferOption } from './types';

/**
 * 같은 회원 · 강사만 변경(강사 간 회차 나누기).
 *
 * 서버 계약(BACKEND_HANDOFF_N §1): 같은 양도 API 에 `toMemberId = 원본 회원`, `toCoachId = 원본과
 * 다른 강사` 를 보낸다. PT(MEMBERSHIP)만 되고, 강사가 비었거나 같으면 서버가 400 으로 거부한다.
 * 그래서 화면은 강사를 **반드시** 고르게 하고 원본 강사는 목록에서 뺀다.
 */
export type TransferRecipientMode = 'OTHER' | 'SAME';

export const isSplitSelectableOption = (
    option: Pick<TicketTransferOption, 'sourceType'> | null | undefined,
): boolean => option?.sourceType === 'MEMBERSHIP';

const normalizeId = (value: unknown): string =>
    typeof value === 'string' ? value.trim().toLowerCase() : '';

/**
 * 이력 행이 같은 회원 나눔인가. 서버는 같은 회원일 때 회원 id 를 소문자로 맞춰 저장하지만,
 * 화면도 대소문자를 무시하고 비교한다(옛 행 보호). 둘 중 하나라도 비면 나눔이 아니다.
 */
export const isSameMemberTransfer = (
    transfer: Pick<TicketTransfer, 'fromMemberId' | 'toMemberId'> | null | undefined,
): boolean => {
    const from = normalizeId(transfer?.fromMemberId);
    const to = normalizeId(transfer?.toMemberId);
    return !!from && from === to;
};

const withCoachSuffix = (name: string | undefined, fallback: string): string => {
    const trimmed = (name || '').trim();
    return trimmed ? `${trimmed} 강사` : fallback;
};

/**
 * 양도 이력의 「누가 → 누구」 라벨.
 * 같은 회원 나눔은 「홍길동 → 홍길동」 대신 「강사 A → 강사 B」로 보여 준다.
 */
export const getTicketTransferPartiesLabel = (
    transfer: Pick<
        TicketTransfer,
        'fromMemberId' | 'toMemberId' | 'fromMemberName' | 'toMemberName' | 'fromCoachName' | 'toCoachName'
    >,
): string => {
    if (isSameMemberTransfer(transfer)) {
        return `${withCoachSuffix(transfer.fromCoachName, '원래 강사')} → ${withCoachSuffix(
            transfer.toCoachName,
            '새 강사',
        )}`;
    }
    return `${(transfer.fromMemberName || '').trim() || '회원'} → ${(transfer.toMemberName || '').trim() || '회원'}`;
};

/**
 * 나누기 확인·요약 문구. 「김강사 강사 → 이강사 강사로 5회 나누기」.
 * 이름 뒤에 항상 「강사」를 붙여 조사가 「로」로 고정된다.
 */
export const getSplitActionLabel = ({
    fromCoachName,
    toCoachName,
    count,
}: {
    fromCoachName?: string;
    toCoachName?: string;
    count: number;
}): string =>
    `${withCoachSuffix(fromCoachName, '원래 강사')} → ${withCoachSuffix(toCoachName, '선택한 강사')}로 ${count}회 나누기`;
